/** Shared domain types for Nevisign Reader */

export type ExtractedDocxComment = {
  word_id: string;
  author: string;
  date?: string;
  body: string;
  quote: string;
  context?: string;
};

export type ExtractedDocxRevision = {
  word_id: string;
  type: "ins" | "del";
  author: string;
  date?: string;
  text: string;
};

export type DocxAnnotations = {
  comments: ExtractedDocxComment[];
  revisions: ExtractedDocxRevision[];
  finalText: string;
  originalText: string;
  counts: {
    comments: number;
    insertions: number;
    deletions: number;
  };
};

export type DocxAnnotationsPayload = {
  comments: ExtractedDocxComment[];
  revisions: ExtractedDocxRevision[];
  finalText: string;
  originalText: string;
};

/** Local edit overlay persisted as JSON (MVP — not written back to DOCX) */
export type LocalEditPatch = {
  id: string;
  /** CSS selector / range hint */
  selectionText: string;
  fontFamily?: string;
  fontColor?: string;
  textAlign?: "right" | "left" | "center" | "justify";
  appliedAt: string;
};

export type DocxViewMode = "continuous" | "paginated";

export type PrintPageSize = "A4" | "Letter";
export type PrintOrientation = "portrait" | "landscape";
export type PrintColorMode = "color" | "grayscale";

export type PrintSettings = {
  pageSize: PrintPageSize;
  orientation: PrintOrientation;
  /** Millimeters */
  marginMm: number;
  includeComments: boolean;
  colorMode: PrintColorMode;
};

export type ReaderSettings = {
  nevisignApiBase: string;
  showTrackChanges: boolean;
  showComments: boolean;
  /** Word: flowing sheet vs print-like page frames */
  docxViewMode: DocxViewMode;
  print: PrintSettings;
};

/** Subset of contract-ai /api/assistant (platform) */
export type AssistantChatMessage = {
  role: "user" | "assistant";
  content: string;
};

export type AssistantRequestBody = {
  message: string;
  path?: string;
  focused_domain_id?: string | null;
  recent?: AssistantChatMessage[];
};

export type AssistantResponse = {
  reply: string;
  actions: unknown[];
  changes: unknown[];
  focus?: {
    domain_id: string;
    tab?: string;
    doc_name?: string;
  };
};

/** Office add-in assistant — document-aware edits (canonical: @nevisign/edit-ops) */
export type {
  ApplyMode,
  OfficeAssistantMode,
  OfficeTextFormat,
  EditOp,
  OfficeEdit,
  OfficeAssistantRequest,
  OfficeAssistantResponse,
  DocumentSectionIndex,
} from "@nevisign/edit-ops";

import type { OfficeEdit as CanonicalOfficeEdit } from "@nevisign/edit-ops";
/** Local alias kept for existing reader imports */
export type ReaderOfficeEdit = CanonicalOfficeEdit;

/** contract-ai GET /api/documents shape (subset) */
export type NevisignDocumentListItem = {
  id: string;
  title: string;
  kind: "contract" | "document";
  kind_label: string;
  status: string;
  status_label: string;
  subject: string;
  jurisdiction: string;
  parties_summary: string;
  created_at: string;
  updated_at: string;
  progress_message: string;
  signed: boolean;
  created_by_user_id?: string | null;
  created_by_name?: string | null;
};

export type NevisignDocumentsResponse = {
  documents: NevisignDocumentListItem[];
  total: number;
};

/** GET /api/office/sync/export/:id subset used by the reader */
export type NevisignDocumentExport = {
  document_id: string;
  body_text: string;
  body_cm?: string;
  title?: string;
  filename?: string;
  text_only?: boolean;
  error?: string;
};

export type CreateDocumentRequest = {
  /** import = save body as final content without AI (Word/Reader cloud-save) */
  mode?: "blank" | "ai" | "import";
  text?: string;
  title?: string;
  attachments?: { name: string; content: string }[];
  allocate_number?: boolean;
  user_key?: string;
};

export type CreateDocumentResponse = {
  id: string;
  document?: { id: string; title?: string };
  error?: string;
};

/** Hosted OTA manifest under /nevisign-reader/version.json */
export type ReaderVersionManifest = {
  versionCode: number;
  versionName: string;
  apkUrl: string;
  forceUpdate?: boolean;
  notes?: string;
};

export type OpenDocumentKind = "pdf" | "docx" | "unknown";

export const DEFAULT_PRINT_SETTINGS: PrintSettings = {
  pageSize: "A4",
  orientation: "portrait",
  marginMm: 12,
  includeComments: true,
  colorMode: "color",
};

/** Max document text sent to office assistant (server also truncates). */
export const OFFICE_DOCUMENT_TEXT_MAX = 24000;
